import * as errors from '@feathersjs/errors'
import Bluebird from 'bluebird'
import { moduleState, state } from 'cerebral'
import debug from 'debug'
import { concat, find, mergeDeepRight, omit, pick, propEq } from 'ramda'
import { isNilOrEmpty, omitBy } from 'ramda-adjunct'
import schemaSettings from '../../../shared/schemas/resort-schema'
import apiClient from '../../lib/util/api-client'
import * as zoho from '../../lib/util/zoho'
const { resortSchema } = schemaSettings

const log = debug('modules:account')

export const resortUpdateFields = ['address', 'logo', 'introLogo', 'birthdayUpdates', 'publishedNameTemplate', 'serviceRequestTypes']
const settingsUpdateFields = ['seasons', 'eventSettings']
const allUpdateFields = concat(resortUpdateFields, settingsUpdateFields)

const omitNilOrEmpty = omitBy(isNilOrEmpty)

export const authenticate = async ({ props, store, get }) => {
  const invitationParams = get(moduleState`invitationParams`) || {}
  const { access_token } = props

  try {
    log('Before authenticate')
    let params = { strategy: 'jwt', accessToken: access_token }
    if (invitationParams?.token) {
      params.invitationToken = invitationParams.token
    }
    const { user } = await apiClient.authenticate(params)
    if (user) {
      zoho.identify(user)
    }
    log('After authenticate', user)
    return { user }
  } catch (error) {
    if (error.data && error.data.isEmailVerified === false) {
      store.set(state`errors.account`, error.message)
      return
    }
    if (error instanceof errors.NotAuthenticated) {
      throw new errors.NotAuthenticated('We were not able to sign you in.', { cause: error })
    } else {
      throw new errors.NotAuthenticated(error.message, { cause: error })
    }
  }
}

export const reauthenticate = async ({ accountService }) => {
  log('Before reauthenticate')
  try {
    const { user, authentication: { payload } = {} } = await accountService.reauthenticate()
    zoho.identify(user)
    log('After reauthenticate', user)
    return { user, payload }
  } catch (error) {
    // Return a NotAuthenticated error to trigger the redirect to the login page
    log('Reauthenticate error', error)
    // Wrap the caught error in a NotAuthenticated error with a message
    throw new errors.NotAuthenticated('Reauthentication error', { cause: error })
  }
}

export const setCurrentUser = ({ props: { user, payload }, store, intercom, get }) => {
  store.set(moduleState`currentUser`, user)
  const organization = get(state`account.organization`)
  const resorts = get(state`account.currentUser.resorts`)

  let companies = [{ id: organization?._id, name: organization?.name }]

  if (organization?.parent) {
    const parent = find(propEq(organization.parent, '_id'), resorts)
    companies.push({ id: parent?._id, name: parent?.name, Parent: true })
  }

  intercom.setUser({ ...user, companies })

  if (payload) {
    store.set(moduleState`payload`, payload)
  }
}

export const checkOrgSlug = ({ get, store }) => {
  const user = get(moduleState`currentUser`)
  const orgSlug = get(moduleState`orgSlug`)
  if (orgSlug) {
    const selectedOrg = find(propEq(orgSlug, 'slug'), user.resorts || [])
    if (!selectedOrg) {
      store.set(moduleState`orgSlug`, null)
    }
  }
}

export const signOut = async ({ accountService, store }) => {
  await accountService.logout()
  store.set(moduleState`currentUser`, null)
  store.set(moduleState`payload`, null)
  store.set(moduleState`redirectedFromPath`, null)
}

export const changeOrganization = ({ props: { orgSlug }, store }) => {
  store.set(moduleState`orgSlug`, orgSlug)
  setTimeout(() => window.location.reload(), 500)
}

export const saveSettings = async ({ props: { values }, resortsService, resortSeasonsService, get, store }) => {
  const organization = get(moduleState`organization`)
  const organizationId = organization?._id

  let updatedSeasons = []
  if (values.seasons) {
    const { seasons } = values
    updatedSeasons = await Bluebird.map(
      seasons,
      async (season, index) => {
        try {
          return resortSeasonsService.patch(season._id, season)
        } catch (error) {
          store.merge(moduleState`seasons.${index}`, { status: 'error', error: error.message })
        }
      },
      { concurrency: 2 }
    )
  }

  const settingsValues = mergeDeepRight(pick(allUpdateFields, organization), values)

  if (organizationId) {
    values = resortSchema.cast(settingsValues, { stripUnknown: true })
    const result = await resortsService.patch(organizationId, values)
    const { resorts } = get(moduleState`currentUser`)
    const updatedResorts = resorts.map((resort) => {
      if (resort?._id === organizationId) {
        return { ...resort, ...omit(['parent'], result) }
      }
      return resort
    })
    store.set(moduleState`currentUser.resorts`, updatedResorts)
  }

  if (updatedSeasons.length > 0) {
    return { entities: { seasons: updatedSeasons } }
  }
}

export const setInvitationParams = async ({ props: { token, email } = {}, store }) => {
  store.merge(moduleState`invitationParams`, omitNilOrEmpty({ token, email }))
}

export const removeInvitationParams = async ({ store }) => {
  store.set(moduleState`invitationParams`, {})
}

export const setTenant = async ({ props: { tenant = {} } = {}, store }) => {
  store.set(moduleState`tenant`, tenant)
}

export const showProfileModalIfNeeded = ({ get, store }) => {
  const currentProfile = get(moduleState`currentProfile`)
  if (currentProfile && !currentProfile.name?.last) {
    store.set(moduleState`profileModalVisible`, true)
  }
}

export const hideProfileModal = ({ store }) => {
  store.set(moduleState`profileModalVisible`, false)
}
